import React, { useCallback, useEffect, useState } from 'react';
import { Card, Col, Row, Table } from 'react-bootstrap-v5';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { getRequestOptions } from '../helpers/Fetchwrapper';
import showLoader from '../helpers/ShowLoader';

interface CheckRow {
	key: string;
	domain: string;
	title: string;
	description: string;
	severity: 'critical' | 'warning';
	count: number;
	status: 'pass' | 'fail';
}

interface DomainSummary {
	domain: string;
	checks: number;
	failed: number;
	findings: number;
}

interface Option {
	value: any;
	label: string;
}

const DOMAIN_ICON: Record<string, string> = {
	Production: 'fas fa-industry',
	Inventory: 'fas fa-boxes',
	Accounting: 'fas fa-balance-scale',
};

interface Props {
	/** Restrict the page to one domain. Omitted on the overview page. */
	domain?: 'Production' | 'Inventory' | 'Accounting';
}

export default function ReconciliationCenter({ domain }: Props) {
	const [checks, setChecks] = useState<CheckRow[]>([]);
	const [summary, setSummary] = useState<DomainSummary[]>([]);
	const [generatedAt, setGeneratedAt] = useState<string>('');

	const [projectOptions, setProjectOptions] = useState<Option[]>([]);
	const [warehouseOptions, setWarehouseOptions] = useState<Option[]>([]);
	const [selectedProject, setSelectedProject] = useState<Option | null>(null);
	const [selectedWarehouse, setSelectedWarehouse] = useState<Option | null>(null);
	const [fromDate, setFromDate] = useState<string>('');
	const [toDate, setToDate] = useState<string>('');

	// Expanded check -> its detail rows.
	const [expanded, setExpanded] = useState<string>('');
	const [detailRows, setDetailRows] = useState<any[]>([]);
	const [detailLoading, setDetailLoading] = useState<boolean>(false);

	const buildParams = useCallback(() => {
		const params = new URLSearchParams();
		if (selectedProject?.value) params.set('id_projects', selectedProject.value);
		if (selectedWarehouse?.value) params.set('id_warehouses', selectedWarehouse.value);
		if (fromDate) params.set('from_date', fromDate);
		if (toDate) params.set('to_date', toDate);
		return params;
	}, [selectedProject, selectedWarehouse, fromDate, toDate]);

	const loadSummary = useCallback(async () => {
		showLoader();
		try {
			const url =
				process.env.REACT_APP_API_URL +
				'reconciliation-center/summary?' +
				buildParams().toString();
			const response = await fetch(url, getRequestOptions());
			const data = await response.json();
			Swal.close();

			if (data.status === 'success') {
				const allChecks: CheckRow[] = data.checks || [];
				const allSummary: DomainSummary[] = data.summary || [];
				setChecks(domain ? allChecks.filter((c) => c.domain === domain) : allChecks);
				setSummary(domain ? allSummary.filter((s) => s.domain === domain) : allSummary);
				setGeneratedAt(data.generated_at || '');
				// A stale open panel would not match the new filters.
				setExpanded('');
				setDetailRows([]);
			} else {
				Swal.fire('Error', data.message || 'Could not load reconciliation summary.', 'error');
			}
		} catch (e) {
			Swal.close();
			Swal.fire('Error', 'Could not reach the reconciliation service.', 'error');
		}
	}, [buildParams, domain]);

	const loadDetail = async (checkKey: string) => {
		if (expanded === checkKey) {
			setExpanded('');
			setDetailRows([]);
			return;
		}

		setExpanded(checkKey);
		setDetailRows([]);
		setDetailLoading(true);

		try {
			const params = buildParams();
			params.set('check', checkKey);
			const url =
				process.env.REACT_APP_API_URL + 'reconciliation-center/detail?' + params.toString();
			const response = await fetch(url, getRequestOptions());
			const data = await response.json();

			if (data.status === 'success') {
				setDetailRows(data.rows || []);
			} else {
				Swal.fire('Error', data.message || 'Could not load findings.', 'error');
			}
		} catch (e) {
			Swal.fire('Error', 'Could not reach the reconciliation service.', 'error');
		} finally {
			setDetailLoading(false);
		}
	};

	async function loadProjectOptions() {
		try {
			const response = await fetch(
				process.env.REACT_APP_API_URL + 'projects',
				getRequestOptions()
			);
			const data = await response.json();
			setProjectOptions(
				(data.data || []).map((p: any) => ({
					value: p.id_projects,
					label: p.project_name,
				}))
			);
		} catch (e) {
			// Filters are optional; the page still works unfiltered.
		}
	}

	// Warehouses are scoped to a project, so they can only be listed once one is
	// chosen. Clearing the project clears the warehouse with it.
	async function loadWarehouseOptions(idProjects: any) {
		if (!idProjects) {
			setWarehouseOptions([]);
			setSelectedWarehouse(null);
			return;
		}

		try {
			const response = await fetch(
				process.env.REACT_APP_API_URL + 'warehouse/' + idProjects + '/list',
				getRequestOptions()
			);
			const data = await response.json();
			setWarehouseOptions(
				(data.data || []).map((w: any) => ({ value: w.id_warehouses, label: w.name }))
			);
		} catch (e) {
			setWarehouseOptions([]);
		}
	}

	useEffect(() => {
		loadProjectOptions();
	}, []);

	useEffect(() => {
		setSelectedWarehouse(null);
		loadWarehouseOptions(selectedProject?.value);
	}, [selectedProject]);

	useEffect(() => {
		loadSummary();
	}, [loadSummary]);

	const totalFindings = summary.reduce((sum, s) => sum + s.findings, 0);
	const totalFailed = summary.reduce((sum, s) => sum + s.failed, 0);
	const totalChecks = summary.reduce((sum, s) => sum + s.checks, 0);

	const exportCsv = () => {
		if (!detailRows.length) return;
		const headers = Object.keys(detailRows[0]);
		const escape = (v: any) => {
			const s = v === null || v === undefined ? '' : String(v);
			return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
		};
		const csv = [
			headers.join(','),
			...detailRows.map((r) => headers.map((h) => escape(r[h])).join(',')),
		].join('\n');

		const link = document.createElement('a');
		link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
		link.download = expanded + '.csv';
		link.click();
	};

	const renderDetailTable = () => {
		if (detailLoading) {
			return <div className="text-center p-5 text-muted">Loading findings...</div>;
		}
		if (!detailRows.length) {
			return <div className="text-center p-5 text-muted">No findings.</div>;
		}

		const headers = Object.keys(detailRows[0]);

		return (
			<>
				<div className="d-flex justify-content-between align-items-center mb-3">
					<span className="text-muted">
						Showing {detailRows.length} finding{detailRows.length === 1 ? '' : 's'}
					</span>
					<button className="btn btn-sm btn-light-primary" onClick={exportCsv}>
						<i className="fas fa-download me-1"></i>
						Export CSV
					</button>
				</div>
				<div style={{ maxHeight: '460px', overflow: 'auto' }}>
					<Table className="table table-row-bordered table-striped gy-2 gs-2" size="sm">
						<thead className="fw-bold" style={{ position: 'sticky', top: 0, background: '#fff' }}>
							<tr>
								{headers.map((h) => (
									<th key={h} className="text-nowrap">
										{h.replace(/_/g, ' ')}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{detailRows.map((row, i) => (
								<tr key={i}>
									{headers.map((h) => {
										const value = row[h];
										const numeric =
											value !== null && value !== '' && !isNaN(Number(value));
										const negative = numeric && Number(value) < 0;
										return (
											<td
												key={h}
												className={
													'text-nowrap ' +
													(numeric ? 'text-end ' : '') +
													(negative ? 'text-danger fw-bold' : '')
												}
											>
												{value === null || value === '' ? '-' : String(value)}
											</td>
										);
									})}
								</tr>
							))}
						</tbody>
					</Table>
				</div>
			</>
		);
	};

	return (
		<>
			<Card className="mb-5">
				<Card.Body>
					<Row className="align-items-end">
						<Col md={3}>
							<label className="form-label fw-bold">Project</label>
							<Select
								options={projectOptions}
								value={selectedProject}
								onChange={(option) => setSelectedProject(option as Option | null)}
								isClearable
								placeholder="All projects"
							/>
						</Col>
						<Col md={3}>
							<label className="form-label fw-bold">Warehouse</label>
							<Select
								options={warehouseOptions}
								value={selectedWarehouse}
								onChange={(option) => setSelectedWarehouse(option as Option | null)}
								isClearable
								placeholder="All warehouses"
							/>
						</Col>
						<Col md={2}>
							<label className="form-label fw-bold">From</label>
							<input
								type="date"
								className="form-control"
								value={fromDate}
								onChange={(e) => setFromDate(e.target.value)}
							/>
						</Col>
						<Col md={2}>
							<label className="form-label fw-bold">To</label>
							<input
								type="date"
								className="form-control"
								value={toDate}
								onChange={(e) => setToDate(e.target.value)}
							/>
						</Col>
						<Col md={2}>
							<button className="btn btn-primary w-100" onClick={loadSummary}>
								<i className="fas fa-sync-alt me-1"></i>
								Run Checks
							</button>
						</Col>
					</Row>
					<div className="text-muted mt-3 fs-8">
						Stock balances use the whole ledger up to the <b>To</b> date. A <b>From</b> date
						filters production and voucher checks only, so that earlier receipts still count
						toward a balance.
					</div>
				</Card.Body>
			</Card>

			<Row className="mb-5">
				<Col md={3}>
					<Card className="h-100">
						<Card.Body className="text-center">
							<div className="fs-2hx fw-bolder text-dark">
								{totalFailed}/{totalChecks}
							</div>
							<div className="text-muted fw-bold">Checks failing</div>
						</Card.Body>
					</Card>
				</Col>
				<Col md={3}>
					<Card className="h-100">
						<Card.Body className="text-center">
							<div
								className={
									'fs-2hx fw-bolder ' + (totalFindings > 0 ? 'text-danger' : 'text-success')
								}
							>
								{totalFindings.toLocaleString()}
							</div>
							<div className="text-muted fw-bold">Total findings</div>
						</Card.Body>
					</Card>
				</Col>
				{summary.map((s) => (
					<Col md={2} key={s.domain}>
						<Card className="h-100">
							<Card.Body className="text-center">
								<div className="text-muted mb-1">
									<i className={DOMAIN_ICON[s.domain] || 'fas fa-list'}></i>
								</div>
								<div
									className={
										'fs-3 fw-bolder ' + (s.findings > 0 ? 'text-danger' : 'text-success')
									}
								>
									{s.findings.toLocaleString()}
								</div>
								<div className="text-muted fw-bold fs-8">{s.domain}</div>
							</Card.Body>
						</Card>
					</Col>
				))}
			</Row>

			{(domain ? [domain] : ['Production', 'Inventory', 'Accounting']).map((domainName) => {
				const domainChecks = checks.filter((c) => c.domain === domainName);
				if (!domainChecks.length) return null;

				return (
					<Card className="mb-5" key={domainName}>
						<Card.Header>
							<Card.Title>
								<i className={(DOMAIN_ICON[domainName] || 'fas fa-list') + ' me-2'}></i>
								{domainName}
							</Card.Title>
						</Card.Header>
						<Card.Body className="p-0">
							<Table className="table table-row-bordered align-middle mb-0">
								<thead className="fw-bold text-muted">
									<tr>
										<th style={{ width: '40px' }}></th>
										<th>Check</th>
										<th style={{ width: '110px' }}>Severity</th>
										<th style={{ width: '110px' }} className="text-end">
											Findings
										</th>
										<th style={{ width: '120px' }} className="text-center">
											Status
										</th>
									</tr>
								</thead>
								<tbody>
									{domainChecks.map((check) => (
										<React.Fragment key={check.key}>
											<tr
												style={{ cursor: check.count > 0 ? 'pointer' : 'default' }}
												onClick={() => check.count > 0 && loadDetail(check.key)}
											>
												<td className="text-center">
													{check.count > 0 && (
														<i
															className={
																'fas ' +
																(expanded === check.key
																	? 'fa-chevron-down'
																	: 'fa-chevron-right')
															}
														></i>
													)}
												</td>
												<td>
													<div className="fw-bold text-dark">{check.title}</div>
													<div className="text-muted fs-8">{check.description}</div>
												</td>
												<td>
													<span
														className={
															'badge ' +
															(check.severity === 'critical'
																? 'badge-light-danger'
																: 'badge-light-warning')
														}
													>
														{check.severity}
													</span>
												</td>
												<td className="text-end fw-bolder">
													{check.count > 0 ? (
														<span className="text-danger">
															{check.count.toLocaleString()}
														</span>
													) : (
														<span className="text-muted">0</span>
													)}
												</td>
												<td className="text-center">
													<span
														className={
															'badge ' +
															(check.status === 'pass'
																? 'badge-light-success'
																: 'badge-light-danger')
														}
													>
														{check.status === 'pass' ? 'Pass' : 'Fail'}
													</span>
												</td>
											</tr>
											{expanded === check.key && (
												<tr>
													<td colSpan={5} className="bg-light p-4">
														{renderDetailTable()}
													</td>
												</tr>
											)}
										</React.Fragment>
									))}
								</tbody>
							</Table>
						</Card.Body>
					</Card>
				);
			})}

			{generatedAt && (
				<div className="text-muted text-end fs-8 mb-5">Last run: {generatedAt}</div>
			)}
		</>
	);
}
